import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import axios from 'axios';
import {formatCrmDateTime, translateCrmLeadStatus, translateCrmTaskColumn} from '../crm/crmFormatters';
import {Button} from 'primereact/button';
import {Card} from 'primereact/card';
import {Column} from 'primereact/column';
import {DataTable} from 'primereact/datatable';
import {ProgressSpinner} from 'primereact/progressspinner';
import {TabPanel, TabView} from 'primereact/tabview';
import {Tag} from 'primereact/tag';
import PartnerCrmProfileComponent from '../crm/audience/PartnerCrmProfileComponent';

const PartnerCrmOverviewComponent = ({partnerId, partner, t}) => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({Authorization: 'Bearer ' + localStorage.getItem('userToken'), 'App-Network': localStorage.getItem('networkId')}), []);
  const roles = (localStorage.getItem('roles') || '').toLowerCase();

  const load = useCallback(() => {
    setLoading(true);
    axios.get(process.env.REACT_APP_API_HOST + `/api/v2/crm/partner/${partnerId}/overview`, {headers})
      .then(response => setData(response.data.data))
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  }, [headers, partnerId, t]);
  useEffect(() => load(), [load]);

  const open = path => history.push(path);
  const partnerParams = `?partnerId=${partnerId}&partnerName=${encodeURIComponent(partner?.name || '')}`;
  const date = value => value ? formatCrmDateTime(value, t) : '—';
  const money = row => row.amount ? new Intl.NumberFormat(undefined, {style: 'currency', currency: row.currency, maximumFractionDigits: row.currency === 'HUF' ? 0 : 2}).format(Number(row.amount)) : '—';

  if (loading && !data) return <div className="partner-crm-loading"><ProgressSpinner/></div>;
  if (!data) return null;
  const permissions = data.permissions || {};

  const leadTable = <DataTable value={data.leads || []} responsiveLayout="scroll" stripedRows emptyMessage={t('crmNoPartnerLeads')}>
    <Column field="status" header={t('crmLeadStatusLabel')} body={row => <Tag value={translateCrmLeadStatus(row.status, t)} severity={row.status === 'QUALIFIED' || row.status === 'CONVERTED' ? 'success' : 'info'}/>}/>
    <Column field="name" header={t('columnName')} bodyClassName="col-body-font-bold"/>
    <Column field="contact_name" header={t('fieldCompanyContact')}/>
    <Column field="owner_name" header={t('crmOwner')}/>
    <Column field="next_action_at" header={t('crmNextStep')} body={row => date(row.next_action_at)}/>
    <Column body={row => <Button icon="pi pi-pencil" className="p-button-text p-button-rounded" onClick={() => open('/crm/lead/update/' + row.id)}/>}/>
  </DataTable>;

  const dealTable = <DataTable value={data.deals || []} responsiveLayout="scroll" stripedRows emptyMessage={t('crmNoPartnerDeals')}>
    <Column field="stage_name" header={t('crmOpportunityStage')} body={row => <Tag value={row.stage_name} severity={row.stage_category === 'WON' ? 'success' : row.stage_category === 'LOST' ? 'danger' : 'info'}/>}/>
    <Column field="name" header={t('columnName')} bodyClassName="col-body-font-bold"/>
    <Column header={t('proformaAmount')} body={money}/>
    <Column field="probability" header={t('crmProbability')} body={row => row.probability != null ? row.probability + '%' : '—'}/>
    <Column field="owner_name" header={t('crmOwner')}/>
    <Column field="next_action_at" header={t('crmNextStep')} body={row => date(row.next_action_at)}/>
    <Column body={row => <Button icon="pi pi-pencil" className="p-button-text p-button-rounded" onClick={() => open('/crm/deal/update/' + row.id)}/>}/>
  </DataTable>;

  const activityTable = <DataTable value={data.activities || []} responsiveLayout="scroll" stripedRows emptyMessage={t('crmNoPartnerActivities')}>
    <Column field="date" header={t('columnDate')} body={row => date(row.date)}/>
    <Column field="subject_name" header={t('columnSubject')} bodyClassName="col-body-font-bold"/>
    <Column field="lead_name" header={t('App\\Crm\\Lead')}/>
    <Column field="employee_name" header={t('columnEmployee')}/>
    <Column field="note" header={t('fieldNote')}/>
  </DataTable>;

  const taskTable = <DataTable value={data.tasks || []} responsiveLayout="scroll" stripedRows emptyMessage={t('crmNoPartnerTasks')}>
    <Column field="successful" header={t('columnStatus')} body={row => <Tag value={row.successful ? translateCrmTaskColumn('DONE', t) : t('crmTaskOpen')} severity={row.successful ? 'success' : 'warning'}/>}/>
    <Column field="subject_name" header={t('columnSubject')} bodyClassName="col-body-font-bold"/>
    <Column header={t('crmRelation')} body={row => row.deal_name || row.lead_name || '—'}/>
    <Column field="deadline" header={t('columnDeadline')} body={row => date(row.deadline)}/>
    <Column field="employee_name" header={t('columnEmployee')}/>
    <Column body={row => <Button icon="pi pi-pencil" className="p-button-text p-button-rounded" onClick={() => open('/crm/task/update/' + row.id)}/>}/>
  </DataTable>;

  return <div className="partner-crm-overview">
    <div className="partner-crm-actions">
      {roles.includes('role_crm_lead_write') && <Button label={t('addLead')} icon="pi pi-user-plus" className="p-button-outlined" onClick={() => open('/crm/lead/create' + partnerParams)}/>} 
      {roles.includes('role_crm_deal_write') && <Button label={t('crmAddOpportunity')} icon="pi pi-briefcase" className="p-button-outlined" onClick={() => open('/crm/deal/create' + partnerParams)}/>} 
      {roles.includes('role_crm_task_write') && <Button label={t('addTask')} icon="pi pi-check-square" onClick={() => open('/crm/task/create' + partnerParams)}/>} 
    </div>
    <TabView renderActiveOnly>
      <TabPanel header={t('crmPartnerOverview')} leftIcon="pi pi-chart-bar">
        <div className="partner-crm-kpis">
          {permissions.leads && <Card><small>{t('crmOpenLeads')}</small><strong>{data.summary.openLeads}</strong></Card>}
          {permissions.deals && <Card><small>{t('crmOpenOpportunities')}</small><strong>{data.summary.openDeals}</strong></Card>}
          {permissions.tasks && <Card><small>{t('crmOpenTasks')}</small><strong>{data.summary.openTasks}</strong></Card>}
          {permissions.activities && <Card><small>{t('crmActivities')}</small><strong>{data.summary.activities}</strong></Card>}
        </div>
        <div className="p-grid">
          {permissions.tasks && <div className="p-col-12 p-xl-6"><h3>{t('crmNextTasks')}</h3>{React.cloneElement(taskTable, {value: (data.tasks || []).filter(row => !row.successful).slice(0, 5)})}</div>}
          {permissions.activities && <div className="p-col-12 p-xl-6"><h3>{t('crmRecentActivities')}</h3>{React.cloneElement(activityTable, {value: (data.activities || []).slice(0, 5)})}</div>}
        </div>
      </TabPanel>
      {roles.includes('role_crm_campaign_read') && <TabPanel header={t('crmPartnerProfile')} leftIcon="pi pi-sliders-h"><PartnerCrmProfileComponent partnerId={partnerId}/></TabPanel>}
      {permissions.leads && <TabPanel header={t('App\\Crm\\Lead')} leftIcon="pi pi-user-plus">{leadTable}</TabPanel>}
      {permissions.deals && <TabPanel header={t('crmOpportunities')} leftIcon="pi pi-briefcase">{dealTable}</TabPanel>}
      {permissions.activities && <TabPanel header={t('crmActivities')} leftIcon="pi pi-comments">{activityTable}</TabPanel>}
      {permissions.tasks && <TabPanel header={t('tasks')} leftIcon="pi pi-check-square">{taskTable}</TabPanel>}
    </TabView>
  </div>;
};

export default withTranslation('common')(PartnerCrmOverviewComponent);
